import React, { useState } from "react";
import { Link } from "react-router-dom";
import landing from "@/content/landing";
import { Section } from "./HeroSection";

const API =
  (typeof window !== "undefined" && window.location && window.location.origin) ||
  process.env.REACT_APP_BACKEND_URL;
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/**
 * Public Waitlist form — fully isolated from Life Core.
 * - No auth imports, no user API, no life-core hooks, no localStorage, no analytics.
 * - Honeypot field `website` (visually hidden, aria-hidden, tabIndex=-1).
 * - Server response is always neutral (202). We surface a generic success state.
 */
export default function WaitlistPreview() {
  const t = landing.waitlist;
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [website, setWebsite] = useState(""); // honeypot
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [clientError, setClientError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setClientError("");
    if (status === "loading" || status === "success") return;
    if (!EMAIL_RE.test(email.trim())) {
      setClientError(t.emailInvalid);
      return;
    }
    if (!consent) {
      setClientError(t.consentRequired);
      return;
    }
    setStatus("loading");
    try {
      const r = await fetch(`${API}/api/waitlist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim(),
          consent: true,
          website, // honeypot passthrough
          locale: "de",
          source: "landing-v1",
        }),
      });
      // Server intentionally returns 202 for all valid-shape requests.
      if (r.status === 202 || r.ok) {
        setStatus("success");
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <Section
      id="waitlist"
      className="bg-gradient-to-b from-white via-ag-paper to-white relative overflow-hidden"
      data-testid="landing-waitlist"
    >
      <div
        className="ag-gold-ambient"
        style={{ width: 520, height: 520, top: -220, right: "50%", transform: "translateX(50%)", opacity: 0.18 }}
        aria-hidden="true"
      />
      <div className="relative mx-auto max-w-xl text-center">
        <div className="ag-overline mb-4">Warteliste</div>
        <h2 className="font-serif text-3xl leading-tight text-ag-ink sm:text-4xl lg:text-5xl">
          {status === "success" ? t.successHeadline : t.headline}
        </h2>
        {status !== "success" && (
          <>
            <p className="mt-5 text-base leading-relaxed text-slate-700 sm:text-[17px]">
              {t.text}
            </p>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">{t.subtext}</p>
          </>
        )}

        {status === "success" ? (
          <div
            className="ag-premium-card mt-10 p-6 text-left sm:p-8"
            aria-live="polite"
            data-testid="landing-waitlist-success"
          >
            <p className="text-base leading-relaxed text-slate-700 sm:text-[17px]">
              {t.successText}
            </p>
          </div>
        ) : (
          <form
            className="ag-premium-card mt-10 space-y-4 p-6 text-left sm:p-8"
            onSubmit={onSubmit}
            noValidate
            aria-describedby="landing-waitlist-status"
            data-testid="landing-waitlist-form"
          >
            {/* Honeypot: visually hidden, not focusable, not announced */}
            <div
              aria-hidden="true"
              style={{
                position: "absolute",
                left: "-10000px",
                top: "auto",
                width: "1px",
                height: "1px",
                overflow: "hidden",
              }}
            >
              <label htmlFor="landing-waitlist-website">{t.honeypotLabel}</label>
              <input
                id="landing-waitlist-website"
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                data-testid="landing-waitlist-honeypot"
              />
            </div>

            <div>
              <label htmlFor="landing-waitlist-email" className="ag-overline">
                {t.emailLabel}
              </label>
              <input
                id="landing-waitlist-email"
                type="email"
                autoComplete="email"
                required
                placeholder={t.emailPlaceholder}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={status === "loading"}
                className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-base text-slate-800 placeholder:text-slate-300 focus:border-ag-ink focus:outline-none focus:ring-2 focus:ring-amber-200 disabled:cursor-not-allowed disabled:opacity-70"
                data-testid="landing-waitlist-email"
              />
            </div>

            <label className="flex items-start gap-3 text-sm text-slate-600">
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                disabled={status === "loading"}
                className="mt-1 h-4 w-4 shrink-0 rounded border-slate-300 text-ag-ink focus:ring-amber-300 disabled:cursor-not-allowed"
                data-testid="landing-waitlist-consent"
              />
              <span>
                {t.consentLabel}{" "}
                <span className="block text-xs text-slate-500 sm:inline">
                  {t.consentPrivacyPrefix}
                  <Link
                    to={t.consentPrivacyHref}
                    className="underline underline-offset-2 hover:text-ag-ink"
                    data-testid="landing-waitlist-privacy-link"
                  >
                    {t.consentPrivacyLabel}
                  </Link>
                  .
                </span>
              </span>
            </label>

            <button
              type="submit"
              disabled={status === "loading"}
              className="inline-flex min-h-12 w-full items-center justify-center rounded-full bg-ag-ink px-6 text-sm font-medium text-white shadow-sm transition hover:bg-ag-ink/90 disabled:cursor-not-allowed disabled:opacity-70"
              data-testid="landing-waitlist-submit"
            >
              {status === "loading" ? t.submittingLabel : t.submitLabel}
            </button>

            <p
              id="landing-waitlist-status"
              aria-live="polite"
              className="min-h-[1.25rem] text-center text-sm text-slate-600"
              data-testid="landing-waitlist-status"
            >
              {clientError || (status === "error" ? t.errorText : "")}
            </p>
          </form>
        )}
      </div>
    </Section>
  );
}
